// React hooks used for callback memoization, derived values, and local form state.
import { useCallback, useMemo, useState } from "react";

// API calls for starting workflows and uploading images.
import {

  // Starts a new AI generation workflow run.
  addAIGenerationRequest,

  // Uploads a new image to the image bucket.
  addNewImage,

} from "api/APIStrichCalls";

// Utility to convert a local file into base64 before upload.
import { toBase64 } from "global/utilities/ConvertImage";



// workflow manager hook to encapsulate all state and logic related to managing AI workflows, their forms, interactions with image bucket etc.
const useAiWorkflowManager = ({
  token,
  workflows,
  toastSuccess,
  toastError,
  toastInfo,
  onWorkflowStarted,
}) => {

  // Current workflow and its form values.
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  // Form values for editable settings of the selected workflow.
  const [workflowFormValues, setWorkflowFormValues] = useState({});

  // Last used main prompt/image so we can carry values across workflow switches.
  const [lastMainTextPrompt, setLastMainTextPrompt] = useState("");

  // Remember last used main image value to carry across workflow switches.
  const [lastMainImageValue, setLastMainImageValue] = useState("");

  // Upload UI state for workflow image fields.
  const [workflowUploadingFields, setWorkflowUploadingFields] = useState({});

  // True if a field looks like an image input in the workflow
  const isWorkflowImageField = useCallback((field) => {

    // Check field type and setting name for common indicators that this is an image input field.
    const type = String(field?.type || "").toLowerCase();

    const settingName = String(field?.setting || "").toLowerCase();

    // Common field types for image inputs include "image", "file", "upload", "bucket_image", 
    // or any setting name that includes "image". 
    return (
      type === "image" ||
      type === "file" ||
      type === "upload" ||
      type === "bucket_image" ||
      settingName.includes("image")
    );

  }, []);



  // True if a field looks like a text prompt input.
  const isWorkflowPromptField = useCallback((field) => {

    // Check field type and setting name for common indicators that this is a prompt field.
    const type = String(field?.type || "").toLowerCase();

    // Common field types for prompts include "prompt", "text", "string", or any setting name that includes "prompt" but not "negative".
    const settingName = String(field?.setting || "").toLowerCase();

    // We want to exclude negative prompts from being treated as the main prompt, so we check that "negative" is not in the setting name.
    return type === "prompt" || (settingName.includes("prompt") && !settingName.includes("negative"));

  }, []);



  // Used to avoid treating negative prompts as the main prompt.
  const isNegativePromptField = useCallback((field) => {

    // Check if the setting name includes both "negative" and "prompt" to identify negative prompt fields.
    const settingName = String(field?.setting || "").toLowerCase();

    return settingName.includes("negative") && settingName.includes("prompt");

  }, []);



  // Pick the best "main prompt" field from editable fields.
  // - prompt fields can vary across workflows, so this finds the most likely candidate to be the main prompt 
  //   for carrying values across workflow switches and for special handling in the UI.
  const getPrimaryWorkflowPromptField = useCallback((fields) => {

    // Keep only prompt-like fields.
    const promptFields = (fields || []).filter((field) => isWorkflowPromptField(field));

    if (promptFields.length === 0) return null;

    // Prefer common main prompt names first from this list - 
    const preferredNames = ["prompt", "positive_prompt", "text_prompt", "design_prompt"];

    // If there's an exact match for a preferred name, use that.
    const exactPreferred = promptFields.find((field) =>

      // Check if the field's setting name matches any of the preferred names (case-insensitive).
      preferredNames.includes(String(field?.setting || "").toLowerCase())
    );

    // If we found an exact preferred field, return it. 
    if (exactPreferred) return exactPreferred;

    // Otherwise pick the first field non-negative prompt if available.
    const nonNegative = promptFields.find((field) => !isNegativePromptField(field));

    // If all prompt fields are negative or we can't determine, just return the first field as a fallback
    return nonNegative || promptFields[0];

  }, [isNegativePromptField, isWorkflowPromptField]);



  // Generic non-empty check for form values.
  const hasWorkflowValue = useCallback((value) => {

    // Check for null/undefined first, then handle strings, and treat other types (numbers, booleans) as having a value if they are not null/undefined.
    if (value === null || value === undefined) return false;

    // For strings, check if there's any non-whitespace content. TRIM to avoid counting spaces as valid input.
    if (typeof value === "string") return value.trim().length > 0;

    // For other types (numbers, booleans), we consider them to have a value as long as they are not null/undefined. This allows 0 or false to be valid inputs if the workflow has such fields.
    return true;

  }, []);



  // Read a reusable positive/main prompt from settings object.
  const getReusableTextPrompt = useCallback((settings) => {

    // If settings is not an object, return empty string.
    if (!settings || typeof settings !== "object") return "";

    // Find keys that look like positive/main prompts.
    const promptKeys = Object.keys(settings).filter((key) => {

      // make it lowercase for easier checks
      const lower = key.toLowerCase();

      // We want to include keys that have "prompt" in their name but exclude those that also have "negative"
      return lower.includes("prompt") && !lower.includes("negative");

    });



    // Use the first non-empty field value.
    const keyWithPrompt = promptKeys.find((key) => {

      // Check if this field has a non-empty value to consider for reuse.
      const value = settings[key];

      // Use the hasWorkflowValue check to determine if this field has a value worth reusing.
      return typeof value === "string" && value.trim().length > 0;

    });

    // If we found a suitable prompt field, return its value; otherwise return empty string.
    return keyWithPrompt ? settings[keyWithPrompt] : "";

  }, []);



  // Find which setting key is the image input for a workflow.
  const getWorkflowImageInputSettingKey = useCallback((workflow) => {

    // Only user-editable settings are shown/used in the form.
    const editableSettings = (workflow?.settings || []).filter((field) => field?.user_editable === "Y" || field?.user_editable === true);

      // Find the first image-like field and return its setting key.
    const imageField = editableSettings.find((field) => isWorkflowImageField(field));

    // If we found an image field, return its setting key; otherwise return null.
    return imageField?.setting || null;

  }, [isWorkflowImageField]);



  // Read image value from settings, preferring workflow-specific image key.
  const getReusableImagePrompt = useCallback((settings, workflow = null) => {

    // If settings is not an object, return empty string.
    if (!settings || typeof settings !== "object") return "";

    // If we have a workflow, try to find its specific image input field and return that value if it's non-empty.
    if (workflow) {

      // If workflow is known, check its image input field first.
      const editableSettings = (workflow.settings || [])
        .filter((field) => field?.user_editable === "Y" || field?.user_editable === true);

      // Find the first image-like field and return its value if it's a non-empty string.
      const imageField = editableSettings.find((field) => isWorkflowImageField(field));

      // If we found an image field, try to get its value and return if it's a non-empty string.
      const workflowImageValue = imageField?.setting ? settings[imageField.setting] : "";

      // Check if the workflow-specific image field has a non-empty value to consider for reuse.
      if (typeof workflowImageValue === "string" && workflowImageValue.trim().length > 0) {

        return workflowImageValue;

      }

    }

    // Fallback: search any setting key that contains "image".
    const imageKeys = Object.keys(settings).filter((key) => key.toLowerCase().includes("image"));

    // Use the first non-empty image-like field value.
    const keyWithImageValue = imageKeys.find((key) => {

      const value = settings[key];

      return typeof value === "string" && value.trim().length > 0;

    });

    // If we found a suitable image field, return its value; otherwise return empty string.
    return keyWithImageValue ? settings[keyWithImageValue] : "";

  }, [isWorkflowImageField]);



  // Build form values for a workflow and carry over reusable prompt/image values.
  // `options.preserveRememberedMainInputs` can be set to false for hard resets
  // (for example: "Clear Settings" should clear prompt/image fields too).
  const getAIWorkFlow = useCallback((workflow, prefilledValues = {}, options = {}) => {

    // By default, when switching workflows or re-selecting the same workflow, we want to preserve main prompt/image values if the new workflow supports it. 
    const { preserveRememberedMainInputs = true } = options;

    // If workflow is missing/invalid, clear current form state.
    if (!workflow || !Array.isArray(workflow.settings)) {

      setSelectedWorkflow(null);

      setWorkflowFormValues({});

      return;

    }

    // Keep only fields users can edit.
    const editableSettings = (workflow.settings || []).filter(

      // Only include fields that are marked as user-editable for the form.
      (field) => field?.user_editable === "Y" || field?.user_editable === true
    );

    // Start with defaults from editable fields.
    const initialValues = editableSettings.reduce((acc, field) => {

      const key = field?.setting;

      // If there's no setting key, we can't use this field in the form values, so skip it.
      if (!key) return acc;

      // Normalize defaults by field type so inputs get usable values.
      if (field.type === "option") {

        // Option fields default to explicit default_value, then first option code.
        const fallbackOption = Array.isArray(field.options) && field.options.length > 0
          ? field.options[0].code
          : "";

        acc[key] = field.default_value ?? fallbackOption ?? "";

      } else if (field.type === "int") {

        // Int defaults parse to number, fallback to 0.
        const parsed = Number.parseInt(field.default_value, 10);

        acc[key] = Number.isNaN(parsed) ? 0 : parsed;

      } else if (field.type === "float") {

        // Float defaults parse to number, fallback to 0.
        const parsed = Number.parseFloat(field.default_value);

        acc[key] = Number.isNaN(parsed) ? 0 : parsed;

      } else if (field.type === "bool" || field.type === "boolean") {

        // Accept common truthy formats returned by API.
        acc[key] = field.default_value === true || field.default_value === "true" || field.default_value === 1 || field.default_value === "1";

      } else {

        // Text/other field defaults.
        acc[key] = field.default_value ?? "";

      }

      return acc;

    }, {});

    // If switching workflows, try to carry current prompt/image values to the new workflow.
    if (selectedWorkflow && selectedWorkflow !== workflow) {

      // Find prompt/image fields for current workflow to read reusable values.
      const sourceEditableSettings = (selectedWorkflow.settings || []).filter(
        (field) => field?.user_editable === "Y" || field?.user_editable === true
      );

      // Get current reusable prompt/image values from the form or fall back to last used values.
      const sourcePromptField = getPrimaryWorkflowPromptField(sourceEditableSettings);

      // For prompt, prefer workflow-specific prompt field but fall back to any prompt looking field or last used value.
      const sourcePromptValue = sourcePromptField?.setting
        ? workflowFormValues[sourcePromptField.setting]
        : (
          getReusableTextPrompt(workflowFormValues)
          || (preserveRememberedMainInputs ? lastMainTextPrompt : "")
        );

      // For image, prefer workflow-specific image key but fall back to any image looking setting or last used value.
      const sourceImageKey = getWorkflowImageInputSettingKey(selectedWorkflow);

      // For image, prefer workflow-specific image key but fall back to any image looking setting or last used value.
      const sourceImageValue = sourceImageKey
        ? (
          hasWorkflowValue(workflowFormValues[sourceImageKey])
            ? workflowFormValues[sourceImageKey]
            : (preserveRememberedMainInputs ? lastMainImageValue : "")
        )
        : (preserveRememberedMainInputs ? lastMainImageValue : "");

      // Find prompt/image fields for the new workflow to know where to copy values.
      const targetPromptField = getPrimaryWorkflowPromptField(editableSettings);

      // For image, find the specific image input field for the new workflow to know where to copy the value.
      const targetImageKey = getWorkflowImageInputSettingKey(workflow);

      // Copy prompt if new workflow has a prompt field.
      if (targetPromptField?.setting && hasWorkflowValue(sourcePromptValue)) {

        // If the new workflow has a prompt field and we have a prompt value to carry over, set it in the initial values for the new workflow form.
        initialValues[targetPromptField.setting] = sourcePromptValue;

      }

      // Copy image if new workflow has an image field.
      if (targetImageKey && hasWorkflowValue(sourceImageValue)) {

        // If the new workflow has an image field and we have an image value to carry over, set it in the initial values for the new workflow form.
        initialValues[targetImageKey] = sourceImageValue;

      }

    }

    // Fallback: use remembered image value if the new workflow supports image input.
    const defaultTargetImageKey = getWorkflowImageInputSettingKey(workflow);

    if (

      // Only apply fallback if switching workflows to avoid overwriting values when re-selecting the same workflow.
      preserveRememberedMainInputs &&
      defaultTargetImageKey &&
      hasWorkflowValue(lastMainImageValue) &&
      !hasWorkflowValue(initialValues[defaultTargetImageKey])     
    ) {

      initialValues[defaultTargetImageKey] = lastMainImageValue;

    }

    // Apply explicit prefilled values (used by popup reuse actions).
    Object.keys(prefilledValues || {}).forEach((key) => {

      // Only apply prefilled values for keys that actually exist in the form to avoid injecting unexpected values.
      if (Object.prototype.hasOwnProperty.call(initialValues, key)) {

        // If the prefilled value is non-empty, use it to override the initial value for this field.
        initialValues[key] = prefilledValues[key];

      }

    });

    // Fallback: use remembered prompt value if the field is still empty.
    const nextPromptField = getPrimaryWorkflowPromptField(editableSettings);

    if (
      preserveRememberedMainInputs &&
      nextPromptField?.setting &&
      hasWorkflowValue(lastMainTextPrompt) &&
      !hasWorkflowValue(initialValues[nextPromptField.setting])
    ) {

      initialValues[nextPromptField.setting] = lastMainTextPrompt;

    }

    const nextPromptValue = nextPromptField?.setting ? initialValues[nextPromptField.setting] : "";

    if (
      preserveRememberedMainInputs &&
      typeof nextPromptValue === "string" &&
      nextPromptValue.trim().length > 0
    ) {

      setLastMainTextPrompt(nextPromptValue);

    }

    const nextImageKey = getWorkflowImageInputSettingKey(workflow);

    const nextImageValue = nextImageKey ? initialValues[nextImageKey] : "";

    if (
      preserveRememberedMainInputs &&
      typeof nextImageValue === "string" &&
      nextImageValue.trim().length > 0
    ) {

      setLastMainImageValue(nextImageValue);

    }

    // Commit selected workflow and reset upload flags for a clean form session.
    setSelectedWorkflow(workflow);

    setWorkflowFormValues(initialValues);

    setWorkflowUploadingFields({});

  }, [
    getPrimaryWorkflowPromptField,
    getReusableTextPrompt,
    getWorkflowImageInputSettingKey,
    hasWorkflowValue,
    lastMainImageValue,
    lastMainTextPrompt,
    selectedWorkflow,
    workflowFormValues,
  ]);

  
  const getAIWorkflowForm = useCallback((workflow) => {

    // This function generates the form JSX
    getAIWorkFlow(workflow);

  }, [getAIWorkFlow]);



  // Update one workflow field, and remember main prompt/image values when relevant.
  const handleWorkflowFieldChange = useCallback((settingKey, value) => {

    // Find editable fields for current workflow.
    const editableSettings = (selectedWorkflow?.settings || [])
      .filter((field) => field?.user_editable === "Y" || field?.user_editable === true);

    // If user updates main prompt field, remember it for future workflow switches.
    const promptField = getPrimaryWorkflowPromptField(editableSettings);

    if (
      promptField?.setting &&
      settingKey === promptField.setting &&
      typeof value === "string" &&
      value.trim().length > 0
    ) {

      setLastMainTextPrompt(value);

    }

    // If user updates main image field, remember it for future workflow switches.
    const imageFieldKey = getWorkflowImageInputSettingKey(selectedWorkflow);

    if (
      imageFieldKey &&
      settingKey === imageFieldKey &&
      typeof value === "string" &&
      value.trim().length > 0
    ) {

      setLastMainImageValue(value);

    }

    // Update only the changed field and keep others as-is.
    setWorkflowFormValues((prev) => ({

      ...prev,
      [settingKey]: value,

    }));

  }, [getPrimaryWorkflowPromptField, getWorkflowImageInputSettingKey, selectedWorkflow]);



  // Reset current workflow form back to its defaults.
  const clearSelectedWorkflowForm = useCallback(() => {

    if (!selectedWorkflow) return;

    // Explicitly clear remembered prompt/image values so "Clear Settings"
    // also clears the main prompt/image fields.
    setLastMainTextPrompt("");

    setLastMainImageValue("");

    // Rebuild form from defaults without remembered prompt/image fallbacks.
    getAIWorkFlow(selectedWorkflow, {}, { preserveRememberedMainInputs: false });

  }, [getAIWorkFlow, selectedWorkflow]);



  // Handle different upload response shapes and extract image URL.
  const extractUploadedImageUrlFromResponse = useCallback((response) => {

    const result = response?.results;

    // Case 1: API returns one object in results.
    if (result && typeof result === "object" && !Array.isArray(result)) {

      return result.image_url ?? result.url ?? null;

    }

    // Case 2: API returns array with first object.
    if (Array.isArray(result) && result.length > 0 && typeof result[0] === "object") {

      return result[0].image_url ?? result[0].url ?? null;

    }

    // Case 3: API returns top-level URL fields.
    return response?.image_url ?? response?.url ?? null;

  }, []);



  // Upload selected local file to bucket, then set returned URL into workflow field.
  const handleWorkflowImageFileSelected = useCallback(async (settingKey, file) => {

    if (!file) return;

    // Mark this specific field as uploading.
    setWorkflowUploadingFields((prev) => ({

      ...prev,
      [settingKey]: true,

    }));

    try {

      // Convert local file to base64 for API upload.
      const image_base64 = await toBase64(file);

      // Build upload payload 
      const uploadPayload = {

        image_base64,
        name: file.name,
        type: "AI",
        category: selectedWorkflow?.code || "AI",

      };

      // Upload to bucket and wait longer timeout for larger images.
      const response = await addNewImage(token, uploadPayload, 300);

      if (!response?.success) {

        // Handle upload failure response.
        toastError({ title: "Error", message: "Could not upload image to bucket." });

        return;

      }

      const uploadedImageUrl = extractUploadedImageUrlFromResponse(response);

      if (!uploadedImageUrl) {

        // Handle case where upload succeeded but no image URL was returned.  
        toastError({ title: "Error", message: "Upload succeeded but no image was returned." });

        return;

      }

      // Save returned URL into the workflow input field.
      handleWorkflowFieldChange(settingKey, uploadedImageUrl);

      // Show success message.
      toastSuccess({ title: "Success", message: "Image uploaded to bucket." });

    } catch (error) {

      // Handle unexpected errors during upload process.
      console.error("Workflow image upload failed:", error);

      toastError({ title: "Error", message: "Failed to upload image." });
  
    } finally {
  
      // Clear uploading state for this field regardless of outcome.
      setWorkflowUploadingFields((prev) => ({

        ...prev,
        [settingKey]: false,

      }));

    }
  }, [
    extractUploadedImageUrlFromResponse,
    handleWorkflowFieldChange,
    selectedWorkflow?.code,
    toastError,
    toastSuccess,
    token,
  ]);

  // Global "any upload running" flag for form submit/button disable states.
  const isAnyWorkflowUploadInProgress = useMemo(() => {

    // Check if any field is currently uploading by looking for any true value in the workflowUploadingFields object.
    return Object.values(workflowUploadingFields).some(Boolean);

  }, [workflowUploadingFields]);



  // Workflows that can accept an image input.
  const imageInputWorkflows = useMemo(() => {

    // Filter workflows to only those that have at least one user-editable image input field, which we can use for carrying over images and showing the bucket picker.
    return (workflows || []).filter((workflow) => Boolean(getWorkflowImageInputSettingKey(workflow)));

  }, [getWorkflowImageInputSettingKey, workflows]);



  // Editable settings for the selected workflow.
  const editableSelectedWorkflowSettings = useMemo(() => {

    // For the currently selected workflow, keep only the settings that are user-editable, as those are the ones we show in the form and allow users to interact with.
    return (selectedWorkflow?.settings || []).filter(
      (field) => field?.user_editable === "Y" || field?.user_editable === true
    );

  }, [selectedWorkflow]);



  // Primary image field for selected workflow (if any).
  const selectedWorkflowImageField = useMemo(() => {

    // Find the first user-editable image input field for the selected workflow, as that is the one we treat as the main image field for special handling in the UI (like showing the bucket picker).
    return editableSelectedWorkflowSettings.find((field) => isWorkflowImageField(field)) || null;

  }, [editableSelectedWorkflowSettings, isWorkflowImageField]);



  // Form fields shown in the text/settings area (image field is rendered separately).
  const selectedWorkflowFormFields = useMemo(() => {

    // For the form area where we show text/settings fields, we want to exclude the main image field if it exists, 
    // since we render that separately with the bucket picker and upload button to the left. 
    if (!selectedWorkflowImageField) {

      return editableSelectedWorkflowSettings;

    }

    // Exclude the main image field from the list of fields rendered in the form, since it's handled separately in the UI.
    return editableSelectedWorkflowSettings.filter(
      (field) => field.setting !== selectedWorkflowImageField.setting
    );

  }, [editableSelectedWorkflowSettings, selectedWorkflowImageField]);



  // Best-effort match from a queue image back to a workflow definition.
  // This allows us to match the best workflow to use even if they change or are removed since the image was generated
  // It tries multiple strategies to find the best matching workflow for a given image based on 
  // available metadata like workflow_id, workflow_code, and available settings.
  const resolveWorkflowForFocusImage = useCallback((image) => {

    const allWorkflows = workflows || [];

    if (!image || allWorkflows.length === 0) return null;

    // First try exact workflow id match from queue item.
    const normalizedWorkflowId = image.workflow_id !== null && image.workflow_id !== undefined
      ? String(image.workflow_id)
      : null;

      // If the image has a workflow_id, try to find a workflow with a matching id. 
    if (normalizedWorkflowId) {

      // Look for a workflow with an id that matches the image's workflow_id. 
      const byId = allWorkflows.find((workflow) => String(workflow?.id) === normalizedWorkflowId);

      if (byId) return byId;

    }

    // Next try workflow code match.
    const imageCode = image.workflow_code || image.job_type || null;

    if (imageCode) {

      // Look for a workflow with a code that matches the image's workflow_code or job_type.
      const byCode = allWorkflows.find((workflow) => workflow?.code === imageCode);

      if (byCode) return byCode;

    }

    // Last fallback: match by overlapping setting keys.
    const imageSettingKeys = Object.keys(image.settings || {});

    // If the image doesn't have any settings, we have no basis to match it to a workflow, so return null.
    if (imageSettingKeys.length === 0) return null;

    // Score workflows by how many setting keys they have in common with the image's settings
    let bestMatch = null;

    // We can have multiple workflows with overlapping setting keys.
    let bestScore = 0;

    // Iterate through all workflows and calculate a score based on how many setting keys they have in 
    // common with the image's settings. Keep track of the workflow with the highest score.
    allWorkflows.forEach((workflow) => {

      // Get the setting keys for this workflow to compare against the image's setting keys.
      const workflowSettingKeys = (workflow.settings || []).map((setting) => setting?.setting).filter(Boolean);

      // Calculate a simple score based on the number of matching setting keys between the workflow and the image.
      const score = workflowSettingKeys.reduce((acc, key) => {

        return acc + (imageSettingKeys.includes(key) ? 1 : 0);

      }, 0);

      // Keep highest-scoring workflow.
      if (score > bestScore) {

        // If this workflow has a higher score than the best we've seen so far, update our best match and best score.
        bestScore = score;

        // This workflow has more overlapping setting keys with the image than any previous workflow we've checked, so we consider it the best match so far.
        bestMatch = workflow;

      }

    });

    // Only return a match if it has at least one overlapping setting key to avoid false matches.
    return bestScore > 0 ? bestMatch : null;

  }, [workflows]);



  // Format setting values for popup display.
  const formatFocusSettingValue = useCallback((field, rawValue) => {

    // For displaying setting values in the focus image popup, we want to handle different types of values gracefully.
    if (rawValue === null || rawValue === undefined || rawValue === "") return "None";

    // For arrays, join values with commas for better readability.
    if (Array.isArray(rawValue)) {

      // If the array has values, join them into a string; otherwise show "None".
      return rawValue.length > 0 ? rawValue.join(", ") : "None";

    }

    // For option fields, try to find the display name for the current value from the field's options.
    if (field?.type === "option" && Array.isArray(field?.options)) {

      // Look for an option where the code matches the raw value, and return its display name if found. This makes option values more user-friendly in the popup.
      const matched = field.options.find((opt) => String(opt?.code) === String(rawValue));

      // If we found a matching option, return its display name; otherwise fall back to showing the raw value as a string.
      return matched?.name || String(rawValue);

    }

    // For objects, stringify them for display. This is a fallback for complex values that don't fit the above cases.
    if (typeof rawValue === "object") {

      return JSON.stringify(rawValue);

    }

    // For strings, numbers, booleans, and other simple types, just convert to string.
    return String(rawValue);

  }, []);



  // Helper for hiding empty values in popup settings list.
  const isEmptyFocusSettingValue = useCallback((rawValue) => {

    // is it empty? null - or undefined counts as empty, as does an empty string or a string with just whitespace, or an empty array. 
    // This helps us avoid showing meaningless values in the focus image popup.
    // Check for null or undefined first, as those are the most straightforward cases of emptiness.
    if (rawValue === null || rawValue === undefined) return true;
   
    // For strings, consider it empty if it's just whitespace. TRIM to avoid counting spaces as valid input.
    if (typeof rawValue === "string") return rawValue.trim() === "";

    // For arrays, consider it empty if it has no items.
    if (Array.isArray(rawValue)) return rawValue.length === 0;
    
    return false;

  }, []);



  // Submit workflow run request using current editable form values.
  const handleGenerateWorkflow = useCallback(async () => {

    // Only consider editable fields when validating and building payload.
    const editableFields = (selectedWorkflow?.settings || [])
      .filter((field) => field?.user_editable === "Y" || field?.user_editable === true);

    // Require at least one image or prompt value.
    const hasImageValue = editableFields.some(

      // Check if any editable field looks like an image input and has a non-empty value, since workflows generally require at least an image or a prompt to generate something meaningful.
      (field) =>
        isWorkflowImageField(field) &&
        hasWorkflowValue(workflowFormValues[field.setting])
    );

    const hasPromptValue = editableFields.some(

      // Check if any editable field looks like a prompt input and has a non-empty value, since workflows generally require at least an image or a prompt to generate something meaningful.
      (field) =>
        isWorkflowPromptField(field) &&
        hasWorkflowValue(workflowFormValues[field.setting])
    );

    if (!hasImageValue && !hasPromptValue) {

      // If we don't have at least an image or a prompt, show an error message and don't submit the workflow, since it's likely to fail or produce meaningless results without any input.
      toastError({ title: "Error", message: "Please provide either an image or a prompt." });

      return;

    }



    // Only send editable settings expected by this workflow.
    const normalizedSettings = editableFields.reduce((acc, field) => {

      // Include only fields that have a valid setting key.
      const key = field?.setting;
      
      // Skip fields without a valid setting key.
      if (!key) return acc;

      // build key/value pairs for the API payload based on current form values for this workflow
      acc[key] = workflowFormValues[key];

      return acc;

    }, {});

    // Build payload with normalized settings and workflow code for the API request.
    const payload = {

      settings: normalizedSettings,
      workflow_code: selectedWorkflow?.code,

    };

    try {

      // Start workflow and notify queue manager to refresh.
      const response = await addAIGenerationRequest(token, payload);

      // Handle workflow start response and show appropriate message.
      if (response.success) {

        // Notify parent to refresh queue and show success message.
        onWorkflowStarted?.();

        // Show success message with next steps.
        toastInfo({ title: "Information", message: "Workflow started. Check latest images for progress." });

      } else {

        // Handle failure response with error message if available.
        toastError({ title: "Error", message: "Could not run workflow: " + (response.error_message || "Unknown error") });

      }

    } catch (error) {

      // Handle unexpected errors during workflow start.
      console.error("Workflow generation failed:", error);

      // Show generic error message.
      toastError({ title: "Error", message: "Something went wrong while running the workflow." });

    }
  }, [
    hasWorkflowValue,
    isWorkflowImageField,
    isWorkflowPromptField,
    onWorkflowStarted,
    selectedWorkflow,
    toastError,
    toastInfo,
    token,
    workflowFormValues,
  ]);

  return {

    selectedWorkflow,
    workflowFormValues,
    workflowUploadingFields,
    selectedWorkflowImageField,
    selectedWorkflowFormFields,
    isAnyWorkflowUploadInProgress,
    imageInputWorkflows,
    isWorkflowImageField,
    isWorkflowPromptField,
    getPrimaryWorkflowPromptField,
    getWorkflowImageInputSettingKey,
    getReusableTextPrompt,
    getReusableImagePrompt,
    resolveWorkflowForFocusImage,
    formatFocusSettingValue,
    isEmptyFocusSettingValue,
    getAIWorkFlow,
    getAIWorkflowForm,
    handleWorkflowFieldChange,
    clearSelectedWorkflowForm,
    handleWorkflowImageFileSelected,
    handleGenerateWorkflow,
    setLastMainTextPrompt,
    setLastMainImageValue,

  };

};

export default useAiWorkflowManager;
